import { MTGCard } from 'types/interfaces/mtgCard';
import { ConsolidatedCard } from 'store/services/cards/search/cardsSearchApi';
import { SetIcon } from 'components/SetIcon';
import './AddCardsSearchListItem.css';

export interface HoverPreview {
  scryfallId?: string;
  imageUrl?: string;
  normalFallbacks?: string[];
  cardName: string;
}

interface Props {
  item: MTGCard | ConsolidatedCard;
  additionalStyle: string;
  onPress: () => void;
  mode?: 'any' | 'specific';
  /** What a click on this printing will add, e.g. "Add as LP · Foil". */
  previewLine?: string;
  onHover?: (preview: HoverPreview) => void;
  onHoverEnd?: () => void;
}

export const AddCardsSearchListItem = ({
  item,
  additionalStyle,
  onPress,
  mode = 'specific',
  previewLine,
  onHover,
  onHoverEnd
}: Props) => {
  // Type guard to check if item is ConsolidatedCard
  const isConsolidated = (item: any): item is ConsolidatedCard => {
    return 'cardName' in item && 'totalVersions' in item;
  };

  if (mode === 'any' && isConsolidated(item)) {
    // Display for "Any Set" mode (consolidated results)
    return (
      <div
        className={`add-cards-search-list-item ${additionalStyle}`}
        onClick={onPress}
        onMouseEnter={() =>
          onHover?.({
            imageUrl: item.sampleImage?.normal || item.sampleImage?.small,
            cardName: item.cardName
          })
        }
        onMouseLeave={onHoverEnd}
      >
        <img
          src={item.sampleImage?.art_crop || item.sampleImage?.small}
          alt={item.cardName + ' image'}
        />
        <div className="add-cards-search-list-item-name">{item.cardName}</div>
        {/* No set information for "Any Set" mode */}
      </div>
    );
  } else {
    // Display for "Specific Set" mode (MTG card results)
    const mtgCard = item as MTGCard;
    // Handle both camelCase and snake_case from backend
    const collectorNumber = mtgCard.collectorNumber || (mtgCard as any).collector_number;
    // Reuse the shared set-symbol assets (individual SVGs from S3), same URL pattern as CardTradeTile
    const setIconUrl = mtgCard.setCode
      ? `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${mtgCard.setCode.toLowerCase()}.svg`
      : null;
    return (
      <div
        className={`add-cards-search-list-item ${additionalStyle}`}
        onClick={onPress}
        onMouseEnter={() =>
          onHover?.({
            scryfallId: mtgCard.scryfallID,
            imageUrl: mtgCard.imageUris?.normal,
            normalFallbacks: mtgCard.normal_fallbacks,
            cardName: mtgCard.name
          })
        }
        onMouseLeave={onHoverEnd}
      >
        <img src={mtgCard?.imageUris?.art_crop} alt={mtgCard.name + ' image'} />
        <div className="add-cards-search-list-item-name">
          {mtgCard.name}
          {previewLine && <div className="add-cards-search-list-item-preview">{previewLine}</div>}
        </div>
        <div className="add-cards-search-list-item-set-container">
          <SetIcon
            setIconUrl={setIconUrl}
            setName={mtgCard.setName}
            size="sm"
            lazy
          />
          <div className="add-cards-search-list-item-set-name">
            {mtgCard.setName}
            {collectorNumber && (
              <span className="add-cards-search-list-item-collector-number">
                #{collectorNumber}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
};
